import { Alert, Button, Radio, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { EmployeeGroup, WizardAction } from '../wizardReducer';
import type { GroupValidation, RosterEmployee } from '../directAllocationResolve';
import TemplateEmployeePicker from './TemplateEmployeePicker';
import styles from './Step3Preview.module.scss';

interface TemplatePreviewProps<Row> {
	template: EmployeeGroup;
	validation: GroupValidation | null;
	lineRows: Row[];
	lineColumns: ColumnsType<Row>;
	lineRowKey: (row: Row) => string;
	// Warning shown on its own line under a row's three inputs (null = none)
	lineNote: (row: Row) => React.ReactNode;
	applyTo: 'all' | 'selected' | null;
	activeEmployees: RosterEmployee[];
	selectedIds: string[];
	targetEmployees: RosterEmployee[];
	existingAllocationIds: Set<string> | null;
	dispatch: React.Dispatch<WizardAction>;
	onSplitEqually: () => void;
}

/**
 * Step 3 for a file with no Employee column: the file's Customer/Class/% lines
 * are one allocation, applied to employees the user explicitly chooses.
 * Two side-by-side cards — "1. Apply to" (who) and "2. Allocation lines" (what)
 * — so neither is buried below the other's scroll area.
 */
export default function TemplatePreview<Row>({
	template,
	validation,
	lineRows,
	lineColumns,
	lineRowKey,
	lineNote,
	applyTo,
	activeEmployees,
	selectedIds,
	targetEmployees,
	existingAllocationIds,
	dispatch,
	onSplitEqually,
}: TemplatePreviewProps<Row>) {
	const replaced = existingAllocationIds
		? targetEmployees.filter((e) => existingAllocationIds.has(e.id))
		: [];
	const unmatchedLines = template.rows.filter((r) => r.customerUnmatched || r.classUnmatched).length;

	return (
		<>
			<Alert
				className={styles['step3__banner']}
				type="warning"
				showIcon
				message="Your file has no Employee column"
				description="Choose which employees this allocation applies to, then check the allocation lines."
			/>

			<div className={styles['step3__template-grid']}>
				<section className={styles['step3__card']}>
					<h4 className={styles['step3__card-title']}>1. Apply to</h4>
					<Radio.Group
						className={styles['step3__apply-options']}
						value={applyTo}
						onChange={(e) => dispatch({ type: 'TEMPLATE_APPLY_TO_CHANGED', applyTo: e.target.value })}
					>
						<Radio value="all">All active employees ({activeEmployees.length})</Radio>
						<Radio value="selected">Only the employees I select</Radio>
					</Radio.Group>

					{applyTo === null && (
						<p className={styles['step3__muted']}>Choose an option to continue.</p>
					)}

					{applyTo === 'all' && (
						<div className={styles['step3__apply-summary']}>
							<p>
								<strong>{targetEmployees.length}</strong> employee(s) will get this allocation.
							</p>
							{replaced.length > 0 && (
								<>
									<p>
										{replaced.length} already have a direct allocation for this pay period, which will be
										replaced:
									</p>
									<div>
										{replaced.map((e) => (
											<Tag key={e.id} color="orange">
												{e.fullName}
											</Tag>
										))}
									</div>
								</>
							)}
						</div>
					)}

					{applyTo === 'selected' && (
						<TemplateEmployeePicker
							employees={activeEmployees}
							selectedIds={selectedIds}
							existingAllocationIds={existingAllocationIds}
							onChange={(employeeIds) => dispatch({ type: 'TEMPLATE_EMPLOYEES_SELECTED', employeeIds })}
						/>
					)}
				</section>

				<section className={styles['step3__card']}>
					<div className={styles['step3__template-header']}>
						<h4 className={styles['step3__card-title']}>2. Allocation lines ({template.rows.length})</h4>
						<Space>
							{validation && (
								<span
									className={`${styles['step3__employee-total']} ${
										validation.isValid
											? styles['step3__employee-total--valid']
											: styles['step3__employee-total--invalid']
									}`}
								>
									Total: {validation.total}%
									{validation.isRoundedTo100 && ' (rounding — saved as 100%)'}
								</span>
							)}
							<Button size="small" disabled={template.rows.length === 0} onClick={onSplitEqually}>
								Split equally
							</Button>
						</Space>
					</div>
					{unmatchedLines > 0 && (
						<p className={styles['step3__muted']}>
							{unmatchedLines} of {template.rows.length} line(s) will use Blank Customer/Class — see the
							warning under each line.
						</p>
					)}
					<Table<Row>
						className={`${styles['step3__table']} ${styles['step3__lines-table']}`}
						dataSource={lineRows}
						columns={lineColumns}
						rowKey={lineRowKey}
						// Always-open expanded row = a full-width line under the three
						// inputs, so the dropdowns stay aligned on one line.
						expandable={{
							expandedRowRender: lineNote,
							rowExpandable: (row) => lineNote(row) !== null,
							expandedRowKeys: lineRows.filter((r) => lineNote(r) !== null).map(lineRowKey),
							showExpandColumn: false,
						}}
						pagination={false}
						scroll={{ y: 300 }}
						size="small"
					/>
				</section>
			</div>
		</>
	);
}
